import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { closeMonth, fetchClosedMonths, fetchForecastYears, reopenMonth } from '@/api/client'
import { ApiError } from '@/api/client'

import { CalendarX2 } from 'lucide-react'
import { AdminSection } from '@/features/admin/AdminSection'

const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

export function ClosedMonthsAdminPage() {
  const qc = useQueryClient()
  const yearsQuery = useQuery({
    queryKey: ['config', 'years'],
    queryFn: () => fetchForecastYears(),
  })

  const openYears = useMemo(() => {
    const list = yearsQuery.data ?? []
    return [...list]
      .filter((y) => y.is_open ?? y.open)
      .map((y) => y.year)
      .sort((a, b) => a - b)
  }, [yearsQuery.data])

  const [selectedYear, setSelectedYear] = useState<number | null>(null)
  const activeYear = selectedYear ?? openYears[0] ?? new Date().getFullYear()

  const closedQuery = useQuery({
    queryKey: ['config', 'closed-months', activeYear],
    queryFn: () => fetchClosedMonths(activeYear),
    enabled: Number.isFinite(activeYear),
  })

  const [error, setError] = useState<string | null>(null)

  const closeMut = useMutation({
    mutationFn: ({ year, month }: { year: number; month: number }) => closeMonth(year, month),
    onSuccess: async () => {
      setError(null)
      await qc.invalidateQueries({ queryKey: ['config', 'closed-months'] })
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Failed to close month')
    },
  })

  const reopenMut = useMutation({
    mutationFn: reopenMonth,
    onSuccess: async () => {
      setError(null)
      await qc.invalidateQueries({ queryKey: ['config', 'closed-months'] })
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Failed to reopen month')
    },
  })

  const closedByMonth = useMemo(() => {
    const map = new Map<number, string>()
    for (const row of closedQuery.data ?? []) {
      map.set(row.month, row.id)
    }
    return map
  }, [closedQuery.data])

  const busy = closeMut.isPending || reopenMut.isPending

  return (
    <AdminSection
      title="Closed months"
      tone="coral"
      icon={CalendarX2}
      lede="Mark months closed to lock owner edits; Finance may still correct with audit."
    >
      {error && (
        <p className="alert alert--error" role="alert">
          {error}
        </p>
      )}

      <div className="admin-form__row">
        <label className="field">
          <span className="field__label">Forecast year</span>
          <select
            className="field__input"
            value={activeYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
          >
            {openYears.length === 0 && <option value={activeYear}>{activeYear}</option>}
            {openYears.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div
        className="closed-month-grid"
        role="group"
        aria-label={`Closed months for ${activeYear}`}
      >
        {MONTH_LABELS.map((label, index) => {
          const month = index + 1
          const closedId = closedByMonth.get(month)
          const isClosed = Boolean(closedId)
          return (
            <button
              key={month}
              type="button"
              className={`closed-month-grid__chip${isClosed ? ' closed-month-grid__chip--closed' : ''}`}
              disabled={busy || closedQuery.isLoading}
              aria-pressed={isClosed}
              onClick={() => {
                if (isClosed && closedId) {
                  reopenMut.mutate(closedId)
                } else {
                  closeMut.mutate({ year: activeYear, month })
                }
              }}
            >
              <span className="closed-month-grid__label">{label}</span>
              <span className="closed-month-grid__state">{isClosed ? 'Closed' : 'Open'}</span>
            </button>
          )
        })}
      </div>
    </AdminSection>
  )
}
